// AddDocumentDialog.tsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Close } from '@mui/icons-material';
import { IDocument } from '../Interfaces/IDocument';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (docs: IDocument[]) => void;
}

export function AddDocumentDialog({ open, onClose, onSave }: Props) {
  const [files, setFiles] = React.useState<File[]>([]);

  const handleClose = () => {
    setFiles([]);
    onClose();
  };

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const next = Array.from(incoming);
    setFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      return [...prev, ...next.filter(f => !existingNames.has(f.name))];
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const removeFile = (idx: number) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    if (files.length === 0) return;

    const promises = files.map(
      file =>
        new Promise<IDocument>(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const dataUrl = reader.result as string;
            const base64 = dataUrl.split(',')[1];
            resolve({
              idDocument: -1,
              FileName: file.name,
              idMIMEType: 1,
              Created_at: new Date().toISOString(),
              idDocumentDirectory: null,
              idLegalTransaction: null,
              perEmail: false,
              isFromTemplate: false,
              Data: base64,
            });
          };
          reader.readAsDataURL(file);
        })
    );

    Promise.all(promises).then(docs => {
      onSave(docs);
      setFiles([]);
      onClose();
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Dokumente hochladen</DialogTitle>
      <DialogContent>
        <Stack spacing={2} alignItems='center' my={2}>
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{
              border: '2px dashed',
              borderColor: 'grey.400',
              borderRadius: 1,
              width: '95%',
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
            }}
          >
            <UploadFileIcon fontSize='large' color='action' />
            <Typography variant='body1' sx={{ mt: 1 }}>
              Dateien hierher ziehen oder
            </Typography>
            <Button
              variant='outlined'
              component='label'
              startIcon={<UploadFileIcon />}
              sx={{ mt: 1 }}
            >
              Dateien auswählen
              <input
                type='file'
                accept='application/pdf'
                hidden
                multiple
                onChange={handleFileChange}
              />
            </Button>
          </Box>

          {files.length > 0 && (
            <Box sx={{ width: '95%' }}>
              <Typography variant='body2' sx={{ mb: 0.5 }}>
                Ausgewählt ({files.length}):
              </Typography>
              <List dense disablePadding>
                {files.map((f, idx) => (
                  <ListItem
                    key={idx}
                    disableGutters
                    secondaryAction={
                      <IconButton size='small' onClick={() => removeFile(idx)}>
                        <Close fontSize='small' />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={f.name} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Abbrechen</Button>
        <Button variant='contained' onClick={handleSave} disabled={files.length === 0}>
          Hochladen ({files.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
